import { forgot, login, logout, reset } from '@/api/auth'
import { USER_CLEAR, USER_LOAD } from '@/store/modules/user/actions'
import { clearApolloStore } from '@/vue-apollo'
import logToConsole from '@/utils/consoleHelper'
import addDays from 'date-fns/addDays'
import format from 'date-fns/format'

//This defines action constants that can be injecting into components and used to call actions.
export const AUTH_REQUEST = 'AUTH_REQUEST'
export const AUTH_LOGOUT = 'AUTH_LOGOUT'
export const AUTH_CLEAR = 'AUTH_CLEAR'
export const AUTH_CHECK = 'AUTH_CHECK'
export const AUTH_FORGOT_PASSWORD_REQUEST = 'AUTH_FORGOT_PASSWORD_REQUEST'
export const AUTH_PASSWORD_RESET_REQUEST = 'AUTH_PASSWORD_RESET_REQUEST'

export default {
  /**
   *  This action performs the actual Authentication request against the auth server
   *
   *  @param {Object} user - The user who we are going to login as.
   *  @param {string} user.username - The username of the user logging in
   *  @param {string} user.password - The password of the user logging in
   *
   *  @returns {Promise<unknown>}
   */
  [AUTH_REQUEST]: ({ commit, getters, dispatch, rootGetters }, user) => {
    return new Promise((resolve, reject) => {
      commit('AUTH_REQUESTED', user.username)
      return login(user.username, user.password)
        .then(response => {
          const token_expires =
            Math.round(Date.now() / 1000) + parseInt(response.expires_in)
          const refresh_token_expires = format(addDays(new Date(), 120), 't')
          commit('AUTH_SUCCESSFUL', {
            username: user.username,
            token: response.access_token,
            token_expires: token_expires,
            refresh_token: response.refresh_token,
            refresh_token_expires: refresh_token_expires
          })
          logToConsole('Running USER_LOAD', 'debug')
          dispatch(USER_LOAD)
            .then(() => {
              logToConsole('Clearing Apollo cache', 'debug')
              clearApolloStore().then(() => {
                logToConsole('Apollo cache cleared', 'debug')
              })
              commit('AUTH_STORE_USER_ID', rootGetters.getUser)
              commit('CHECK_IF_USER_IS_MASQUERADING', getters.getAuthUserID)
              return resolve()
            })
            .catch(error => {
              return reject(error)
            })
        })
        .catch(error => {
          commit('AUTH_FAILURE', user.username)
          dispatch(AUTH_CLEAR).then(() => {
            return reject(error)
          })
        })
    })
  },
  /**
   *  This action performs the a logout request on the auth server
   *
   *  @returns {Promise<unknown>}
   */
  [AUTH_LOGOUT]: ({ commit, dispatch }) => {
    return new Promise((resolve, reject) => {
      commit('AUTH_LOGOUT_REQUESTED')
      logout()
        .then(() => {
          commit('AUTH_LOGOUT_SUCCESSFUL')
          dispatch(AUTH_CLEAR)
            .then(() => {
              return resolve()
            })
            .catch(() => {
              return resolve()
            })
        })
        .catch(() => {
          commit('AUTH_LOGOUT_FAILURE')
          dispatch(AUTH_CLEAR)
            .then(() => {
              return resolve()
            })
            .catch(error => {
              return reject(error)
            })
        })
    })
  },
  /**
   *  This action clears all the stored auth credentials
   *
   *  @returns {Promise<unknown>}
   */
  [AUTH_CLEAR]: ({ commit, dispatch }) => {
    return new Promise((resolve, reject) => {
      logToConsole('Clearing Apollo cache', 'debug')
      clearApolloStore().then(() => {
        logToConsole('Apollo cache cleared', 'debug')
      })
      commit('AUTH_RESET')
      dispatch(USER_CLEAR)
        .then(response => {
          return resolve(response)
        })
        .catch(error => {
          return reject(error)
        })
    })
  },
  /**
   *  This action checks if the stored authentication credentials are valid or if the user needs to login
   *
   *  @returns {Promise<unknown>}
   */
  [AUTH_CHECK]: ({ dispatch, commit, getters, state }) => {
    return new Promise((resolve, reject) => {
      if (getters.isAuthenticated) {
        commit('AUTH_SUCCESSFUL', {
          username: state.username,
          token: state.token,
          token_expires: state.token_expires,
          refresh_token: state.refresh_token,
          refresh_token_expires: state.refresh_token_expires
        })
        logToConsole('Running USER_LOAD', 'debug')
        dispatch(USER_LOAD)
          .then(() => {
            commit('CHECK_IF_USER_IS_MASQUERADING', getters.getAuthUserID)
            return resolve()
          })
          .catch(() => {
            return reject()
          })
      } else {
        dispatch(AUTH_CLEAR)
          .then(() => {
            return reject()
          })
          .catch(() => {
            return reject()
          })
      }
    })
  },
  /**
   *  This action initiates the password reset process
   *
   *  @param {Object} forgot_password - The user who we are going to reset.
   *  @param {string} forgot_password.email - The email of the user we are trying to reset
   *
   *  @returns {Promise<unknown>}
   */
  [AUTH_FORGOT_PASSWORD_REQUEST]: ({ commit }, forgot_password) => {
    return new Promise((resolve, reject) => {
      commit('AUTH_FORGOT_PASSWORD_REQUESTED', forgot_password.email)
      return forgot(forgot_password.email)
        .then(response => {
          commit('AUTH_FORGOT_PASSWORD_SUCCESS', forgot_password.email)
          return resolve(response)
        })
        .catch(error => {
          commit('AUTH_FORGOT_PASSWORD_FAILURE', forgot_password.email)
          return reject(error)
        })
    })
  },
  /**
   *  This action takes a valid reset token and lets the user set a new password
   *
   *  @param {Object} password_reset - The user who we are going to reset.
   *  @param {string} password_reset.email - The email of the user we are resetting
   *  @param {string} password_reset.password - The new password that the user wants
   *  @param {string} password_reset.password_confirmation - The confirmed password
   *  @param {string} password_reset.token - The reset token that was provided to the user by email
   *
   *  @returns {Promise<unknown>}
   */
  [AUTH_PASSWORD_RESET_REQUEST]: ({ commit }, password_reset) => {
    return new Promise((resolve, reject) => {
      commit('AUTH_PASSWORD_RESET_REQUESTED', password_reset.email)
      return reset(
        password_reset.email,
        password_reset.password,
        password_reset.password_confirmation,
        password_reset.token
      )
        .then(response => {
          commit('AUTH_PASSWORD_RESET_SUCCESS', password_reset.email)
          return resolve(response)
        })
        .catch(error => {
          commit('AUTH_PASSWORD_RESET_FAILURE', password_reset.email)
          return reject(error)
        })
    })
  }
}
